import { NavigationDrawer } from 'theme';

// Cache promises, not DOM nodes: both viewport renderings can use the same response.
const productRequests = new Map();

function requestProducts(source) {
  const url = new URL(source, window.location.origin);
  if (url.origin !== window.location.origin || !url.pathname.includes('/collections/')) {
    return Promise.reject(new Error('A same-origin collection URL is required'));
  }
  url.searchParams.set('section_id', 'yg-mega-menu-products');
  url.searchParams.delete('sections');
  url.searchParams.set('page', '1');
  url.hash = '';
  const key = url.href;
  if (!productRequests.has(key)) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    const request = fetch(url, { credentials: 'same-origin', signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Products could not be loaded');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const products = document.querySelector('[data-yg-menu-products]');
        if (!products) throw new Error('Missing product section');
        return products.outerHTML;
      })
      .catch((error) => { productRequests.delete(key); throw error; })
      .finally(() => clearTimeout(timeout));
    productRequests.set(key, request);
  }
  return productRequests.get(key);
}

class YGMegaMenu extends HTMLElement {
  connectedCallback() {
    this._events?.abort();
    this._events = new AbortController();
    const options = { signal: this._events.signal };
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-series-tab]');
      if (tab) this.select(tab.dataset.seriesTab);
      const retry = event.target.closest('[data-retry]');
      if (retry) this.loadProducts(retry.closest('[data-products-container]'));
    }, options);
    this.addEventListener('pointerover', (event) => {
      const tab = event.target.closest('[data-series-tab]');
      if (tab && event.pointerType === 'mouse' && window.matchMedia('(pointer: fine)').matches) this.select(tab.dataset.seriesTab);
    }, options);
    this.addEventListener('focusin', (event) => {
      const tab = event.target.closest('[data-series-tab]');
      if (tab) this.select(tab.dataset.seriesTab);
    }, options);
    this.addEventListener('keydown', (event) => this._onKeyDown(event), options);
    this.addEventListener('toggle', (event) => {
      const accordion = event.target;
      if (!accordion.matches('details[data-series-panel]') || !accordion.open) return;
      this.querySelectorAll('details[data-series-panel]').forEach((other) => {
        if (other !== accordion) other.open = false;
      });
      if (this._visible()) this.loadProducts(accordion.querySelector('[data-products-container]'));
    }, { ...options, capture: true });
    this._owner = this.closest('details[is="mega-menu-disclosure"]');
    this._owner?.addEventListener('toggle', (event) => {
      if (event.target === this._owner && this._owner.open) this.activate();
    }, options);
    this._drawer = this.closest('yg-navigation-drawer');
    this._drawer?.addEventListener('yg-menu:activate', () => {
      if (this._visible()) this.activate();
    }, options);
    this.closest('.shopify-section')?.addEventListener('shopify:block:select', (event) => {
      if (this._owner && event.detail.blockId === this.dataset.blockId) this._owner.open = true;
    }, options);
    if (this._visible()) this.activate();
  }

  disconnectedCallback() {
    this._events?.abort();
    this.querySelectorAll('[data-products-url]').forEach((container) => {
      container.removeAttribute('aria-busy');
      container.querySelector('[data-load-status]').hidden = true;
    });
  }

  _visible() {
    const visible = this.checkVisibility
      ? this.checkVisibility({ checkVisibilityCSS: true })
      : this.getClientRects().length > 0 && getComputedStyle(this).visibility !== 'hidden';
    return this.isConnected && visible &&
      (!this._owner || this._owner.open) && (!this._drawer || this._drawer.open);
  }

  activate() {
    const active = this.getAttribute('mode') === 'mobile'
      ? this.querySelector('details[open]')
      : this.querySelector('[data-series-panel]:not([hidden])');
    if (active) this.loadProducts(active.querySelector('[data-products-container]'));
  }

  select(index, focus = false) {
    const tabs = [...this.querySelectorAll('[data-series-tab]')];
    const selected = tabs.find((tab) => tab.dataset.seriesTab === String(index));
    if (!selected) return;
    tabs.forEach((tab) => {
      const active = tab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.querySelectorAll('[data-series-panel]').forEach((panel) => { panel.hidden = panel.dataset.seriesPanel !== String(index); });
    if (focus) selected.focus();
    this.activate();
  }

  _onKeyDown(event) {
    if (event.key === 'Escape' && this._owner?.open) {
      event.stopPropagation();
      this._owner.open = false;
      this._owner.querySelector('summary').focus();
      return;
    }
    const tab = event.target.closest('[data-series-tab]');
    if (!tab) return;
    const tabs = [...this.querySelectorAll('[data-series-tab]')];
    let index = tabs.indexOf(tab);
    if (event.key === 'ArrowDown') index = (index + 1) % tabs.length;
    else if (event.key === 'ArrowUp') index = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = tabs.length - 1;
    else return;
    event.preventDefault();
    this.select(tabs[index].dataset.seriesTab, true);
  }

  async loadProducts(container) {
    if (!container?.dataset.productsUrl || container.dataset.loaded || container.getAttribute('aria-busy') === 'true') return;
    const signal = this._events.signal;
    container.setAttribute('aria-busy', 'true');
    container.querySelector('[data-load-status]').hidden = false;
    container.querySelector('[data-load-error]').hidden = true;
    try {
      const html = await requestProducts(container.dataset.productsUrl);
      if (signal.aborted || !this.isConnected) return;
      const template = document.createElement('template');
      template.innerHTML = html;
      container.prepend(template.content.cloneNode(true));
      container.dataset.loaded = 'true';
    } catch {
      if (!signal.aborted && this.isConnected) container.querySelector('[data-load-error]').hidden = false;
    } finally {
      if (!signal.aborted && this.isConnected) {
        container.setAttribute('aria-busy', 'false');
        container.querySelector('[data-load-status]').hidden = true;
      }
    }
  }
}

class YGNavigationDrawer extends NavigationDrawer {
  connectedCallback() {
    super.connectedCallback();
    const options = { signal: this._abortController.signal };
    this._header = document.getElementById(this.dataset.headerSection);
    this.addEventListener('dialog:after-show', () => this._setPanelAccessibility(this._activePanel || 0), options);
    this.addEventListener('dialog:after-hide', () => {
      this._activePanel = 0;
      this._entryButtons?.clear();
      this._setPanelAccessibility(0);
      this.querySelectorAll('yg-mega-menu').forEach((menu) => {
        menu.querySelectorAll('details').forEach((accordion, index) => { accordion.open = index === 0; });
      });
      this.controls.forEach((button) => {
        button.closest('store-header')?.classList.toggle('is-filled', Boolean(this._headerWasFilled));
      });
      this._headerWasFilled = undefined;
    }, options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) this.hide();
    }, options);
    window.addEventListener('resize', () => {
      if (window.matchMedia('(min-width: 1150px)').matches && this.classList.contains('lg:hidden')) {
        if (this.open) this.hide();
      } else if (this.open) this._setInitialPosition();
      this._header?.querySelectorAll('details[is="mega-menu-disclosure"][open]').forEach((menu) => {
        if (window.matchMedia('(max-width: 1149px)').matches) menu.open = false;
      });
    }, options);
    this._headerObserver = new ResizeObserver(() => { if (this.open) this._setInitialPosition(); });
    if (this._header) this._headerObserver.observe(this._header);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._panelFocusFrame);
    this._headerObserver?.disconnect();
    super.disconnectedCallback();
  }

  _setInitialPosition() {
    super._setInitialPosition();
    if (!window.matchMedia('(max-width: 1149px)').matches) return;
    const bottom = Math.max(0, this._header?.getBoundingClientRect().bottom || 0);
    this.style.setProperty('--yg-menu-header-bottom', `${bottom}px`);
    this.style.top = `${bottom}px`;
    this.style.bottom = 'auto';
    this.controls.forEach((button) => {
      const header = button.closest('store-header');
      if (!header) return;
      if (this._headerWasFilled === undefined) this._headerWasFilled = header.classList.contains('is-filled');
      header.classList.add('is-filled');
    });
  }

  _setPanelAccessibility(index) {
    this.querySelectorAll('.panel').forEach((panel, current) => {
      panel.inert = current !== Number(index);
      panel.setAttribute('aria-hidden', String(panel.inert));
    });
  }

  switchToPanel(panelIndex, linkListIndex = null) {
    super.switchToPanel(panelIndex, linkListIndex);
    this._activePanel = Number(panelIndex);
    this._setPanelAccessibility(panelIndex);
    if (linkListIndex !== null) {
      const entry = [...this.querySelectorAll('button[data-panel]')].find((button) => button.dataset.panel === `${panelIndex}-${linkListIndex}`);
      this._entryButtons ??= new Map();
      this._entryButtons.set(Number(panelIndex), entry);
      this.setExpanded(entry);
    } else this.setExpanded();
    const panel = this.querySelectorAll('.panel')[Number(panelIndex)];
    cancelAnimationFrame(this._panelFocusFrame);
    const focusPanel = () => {
      if (!this.open || panel.inert) return;
      // Native animation timing can be delayed by a busy storefront. Focus only
      // after the incoming panel actually reaches the drawer's visible bounds.
      const position = panel.getBoundingClientRect();
      const drawerPosition = this.getBoundingClientRect();
      if (getComputedStyle(panel).visibility !== 'visible' || Math.abs(position.left - drawerPosition.left) > 1) {
        this._panelFocusFrame = requestAnimationFrame(focusPanel);
        return;
      }
      const target = linkListIndex === null
        ? this._entryButtons?.get(Number(panelIndex) + 1)
        : panel.querySelector('.panel__wrapper:not([hidden]) button');
      target?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('yg-menu:activate'));
    };
    this._panelFocusFrame = requestAnimationFrame(focusPanel);
  }
}

if (!customElements.get('yg-mega-menu')) customElements.define('yg-mega-menu', YGMegaMenu);
if (!customElements.get('yg-navigation-drawer')) customElements.define('yg-navigation-drawer', YGNavigationDrawer);
